/**
 * ComparisonProsCons — column-per-site read-only digest of pros / cons.
 * Each card stacks two panels (Pros on top, Cons on bottom) with the
 * items sorted per H1's rules:
 *
 *   pros desc by upvotes
 *   cons desc by impact level (HIGH > MEDIUM > LOW > unset)
 *
 * Read-only — no add/edit/delete here. Users go to the per-evaluation
 * Pros & Cons tab (or `/proscons`) to make changes. F5 is a comparison
 * digest, not an editor.
 *
 * Linear: MYD-220 (F5).
 */

import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { CheckCircle2, XCircle, ThumbsUp } from 'lucide-react'
import { cn } from 'lib/utils'
import { fetchSiteProsCons } from 'slices/proConSlice'

const IMPACT_RANK: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 }

const IMPACT_COLORS: Record<string, string> = {
	HIGH: 'bg-red-50 text-red-700 border-red-200',
	MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
	LOW: 'bg-gray-100 text-gray-600 border-gray-200',
}

interface ComparisonProsConsProps {
	evalId: string
	sites: any[]
}

const ComparisonProsCons = ({ evalId, sites }: ComparisonProsConsProps) => {
	const dispatch = useDispatch()
	const bySite: Record<string, any[]> = useSelector(
		(state: any) => state.proCons?.bySite || {},
	)

	useEffect(() => {
		for (const site of sites) {
			;(dispatch as any)(fetchSiteProsCons({ evalId, siteId: site._id }))
		}
	}, [dispatch, evalId, sites])

	const useScrollStrip = sites.length >= 4

	return (
		<div
			className={
				useScrollStrip
					? 'flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
					: 'flex gap-3 overflow-x-auto lg:overflow-x-visible pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
			}
		>
			{sites.map((site) => (
				<SiteCard key={site._id} site={site} items={bySite[site._id] || []} />
			))}
		</div>
	)
}

interface SiteCardProps {
	site: any
	items: any[]
}

const SiteCard = ({ site, items }: SiteCardProps) => {
	const pros = useMemo(
		() =>
			items
				.filter((i) => i.type === 'PRO')
				.slice()
				.sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0)),
		[items],
	)
	const cons = useMemo(
		() =>
			items
				.filter((i) => i.type === 'CON')
				.slice()
				.sort(
					(a, b) =>
						(IMPACT_RANK[b.impactLevel] ?? 0) - (IMPACT_RANK[a.impactLevel] ?? 0),
				),
		[items],
	)

	return (
		<Card className="flex flex-col w-[300px] shrink-0 lg:w-auto lg:shrink lg:flex-1 min-h-[360px] overflow-hidden">
			<div className="px-3 py-2 border-b border-gray-200 bg-white">
				<h4
					className="text-sm font-semibold text-gray-900 truncate"
					title={site.name}
				>
					{site.name}
				</h4>
			</div>

			<Panel
				title="Pros"
				count={pros.length}
				items={pros}
				type="PRO"
				accent="text-green-700"
				Icon={CheckCircle2}
				emptyText="No pros."
			/>
			<Panel
				title="Cons"
				count={cons.length}
				items={cons}
				type="CON"
				accent="text-red-700"
				Icon={XCircle}
				emptyText="No cons."
			/>
		</Card>
	)
}

interface PanelProps {
	title: string
	count: number
	items: any[]
	type: 'PRO' | 'CON'
	accent: string
	Icon: typeof CheckCircle2
	emptyText: string
}

const Panel = ({ title, count, items, type, accent, Icon, emptyText }: PanelProps) => (
	<div className="flex flex-col flex-1 min-h-0">
		<div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-100">
			<Icon className={cn('w-3.5 h-3.5', accent)} />
			<h5 className={cn('text-[11px] font-semibold uppercase tracking-wide', accent)}>
				{title} ({count})
			</h5>
		</div>
		<div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5 bg-gray-50/40">
			{items.length === 0 ? (
				<div className="text-center py-3 text-xs text-gray-400">{emptyText}</div>
			) : (
				items.map((item) => (
					<div
						key={item._id}
						className="px-2.5 py-2 rounded-md border border-gray-100 bg-white"
					>
						<div className="flex items-center gap-1.5 flex-wrap mb-1">
							<Badge className="text-[10px] bg-gray-100 text-gray-600">
								{item.category}
							</Badge>
							{type === 'CON' && item.impactLevel && (
								<Badge
									className={cn(
										'text-[10px] border',
										IMPACT_COLORS[item.impactLevel] || IMPACT_COLORS.LOW,
									)}
								>
									{item.impactLevel}
								</Badge>
							)}
							{type === 'PRO' && (
								<span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-gray-500">
									<ThumbsUp className="w-3 h-3" />
									{item.votes ?? 0}
								</span>
							)}
						</div>
						<p className="text-xs text-gray-800 break-words leading-snug">
							{item.description}
						</p>
					</div>
				))
			)}
		</div>
	</div>
)

export default ComparisonProsCons
